/**
 * SOLID Review
 * S — Strong. One concern: the presentation state screens share — the server-busy banner the Apollo client's slow-request link
 *     raises — and nothing else.
 * O — Good. A new shared presentation fact is one field and one setter.
 * L — Strong. A view model like any other (`IServiceViewModel`): the binding cannot tell it from a screen's own.
 * I — Strong. One setter, the view-model surface.
 * D — Strong. Its own state collaborator; no store library, no global.
 */
import { ObservableState } from '../viewmodels/ObservableState';
import type { IServiceViewModel } from '../viewmodels/IServiceViewModel';

export interface IStateUi {
  /** What the server-busy banner says, or null while it is down. */
  readonly connectionBanner: string | null;
}

/**
 * THE SCREENS' SHARED PRESENTATION STATE (frontend-framework-oo P1, 2026-10-29, §28.293) — `stores/uiStore.ts` until P1: a zustand
 * store built at module load, 255 lines, of which ONE field had a reader. Toasts (with their module-level counter), a modal stack,
 * loading flags, overlay flags, a sidebar and a batch reset had no production consumer, and `isConnectionBannerVisible` was written
 * and never read; §29.4b deletes them rather than moving them (DEVIATION, flagged in the plan's revision: the plan moved the store
 * whole, its toast counter with it). Built once by the composition root — whose slow-request handlers write it — and handed to the
 * tree by the `ViewModelFactory`.
 */
/** The banner down: what a page starts with. */
const BANNER_DOWN: IStateUi = { connectionBanner: null };

export class UiState implements IServiceViewModel<IStateUi> {
  private readonly state = new ObservableState<IStateUi>(BANNER_DOWN);

  setConnectionBanner(message: string | null): void {
    const change: Partial<IStateUi> = { connectionBanner: message };
    this.state.update(change);
  }

  getSnapshot(): IStateUi {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  /** Shared by every view that reads it: nothing to start or stop for one of them. */
  attach(): void {}

  detach(): void {}
}
